import { inject, Injectable } from '@angular/core';
import { BatchDomain, CtBatch, CtBatchSource, CtDocument } from '@ps-gui/claims-terminal-shared';
import { PersistDataService } from './persist-data.service';
import { associateBy } from '@ps-gui/shared-utils';

@Injectable({
  providedIn: 'root',
})
export class CtBatchStoreService {
  private readonly persistDataService = inject(PersistDataService);

  /**
   * This variable is initialized by the route resolver for `batch/:id` such
   * that all services injected by components rendered by this route can use
   * this domain property, for example, to load the correct extraction config.
   */
  private _ctBatchDomain!: BatchDomain;

  private _ctBatch: CtBatch = {} as CtBatch;
  private _batchPageCount = 0;
  private documentsByPageId: Record<string, CtDocument> = {};
  private documentsById: Record<string, CtDocument> = {};

  private _ctBatchId: string | null = this.persistDataService.ctBatchId;

  set ctBatchDomain(domain: BatchDomain) {
    this._ctBatchDomain = domain;
  }

  get ctBatchDomain(): BatchDomain {
    return this._ctBatchDomain;
  }

  get ctBachSource(): CtBatchSource {
    return this._ctBatch.source;
  }

  set ctBatch(ctBatch: CtBatch) {
    this._ctBatch = ctBatch;
    this._batchPageCount = ctBatch.documents.reduce(
      (sum, document) => sum + document.pages.length,
      0,
    );

    this.documentsByPageId = Object.fromEntries(
      ctBatch.documents.flatMap((document) =>
        document.pages.map((page) => [page.id, document] as const),
      ),
    );
    this.documentsById = associateBy(ctBatch.documents, (document) => document.id);
  }

  get ctBatch(): CtBatch {
    return this._ctBatch;
  }

  get ctBatchPageCount(): number {
    return this._batchPageCount;
  }

  setCtBatchId(ctBatchId: string): Promise<void> {
    this._ctBatchId = ctBatchId;
    return this.persistDataService.setCtBatchId(ctBatchId);
  }

  get ctBatchId(): string | null {
    return this._ctBatchId;
  }

  getDocumentForPage(pageId: string): CtDocument {
    if (!(pageId in this.documentsByPageId)) {
      throw new Error(`No document found for pageId ${pageId}`);
    }
    return this.documentsByPageId[pageId];
  }

  getDocumentById(id: string) {
    return this.documentsById[id];
  }
}
